import React, {useMemo, useState} from "react";
import {FieldArrayWithId} from "react-hook-form";
import {getSign} from "view/apps/Supplier/pages/shared/SurchargeModal/shared";
import {currencySymbols} from "constraints/general";
import FlexBox from "view/components/Layout/FlexBox";
import {Divider, IconButton} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import {Currency, IStage, SurchargeType} from "@evennt-tech/api-v2-core";
import AddOrEdit from "view/apps/Supplier/pages/shared/SurchargeModal/Components/AddOrEdit";
import {grey} from "@material-ui/core/colors";
import Alert from "view/components/Alert";

type SurchargeProps = {
  fields: FieldArrayWithId<{stages: IStage[]}, "stages", "id">[];
  currency: Currency;
  update: (index: number, stage: IStage) => void;
  remove: (index: number) => void;
  isUnpublishTaken: boolean;
  takenDates: number[];
};

function List({fields, ...rest}: SurchargeProps) {
  const list = fields.map((field, index) => (
    <Item key={field.id} field={field} index={index} {...rest} />
  ));

  return (
    <FlexBox column>
      {list.length ? (
        list
      ) : (
        <FlexBox>
          <Alert severity="info">
            A surcharge will allow you to create steps in either Percentage or
            Fixed Fee and apply the setting before the Build Up begins.
          </Alert>
        </FlexBox>
      )}
    </FlexBox>
  );
}

type ItemProps = Omit<SurchargeProps, "fields"> & {
  field: IStage;
  index: number;
};

function Item({
  field,
  index,
  remove,
  update,
  currency,
  takenDates,
  isUnpublishTaken,
}: ItemProps) {
  const [isEditOpen, setIsEditOpen] = useState(false);

  const change = getChange(field, currency);

  const takenDatesWithoutEditable = useMemo(() => {
    const copy = [...takenDates];
    const indexOfCurrentDate = copy.findIndex(
      (p) => p === field.daysBeforeBuild
    );
    if (indexOfCurrentDate !== -1) {
      copy.splice(indexOfCurrentDate, 1);
    }
    return copy;
  }, [takenDates, field]);

  return (
    <FlexBox column>
      <FlexBox alignCenter justifyContent="space-between">
        <span>
          Stage {index + 1} {change}, {field.daysBeforeBuild} days before Build
          Up starts
        </span>
        <FlexBox alignCenter>
          <IconButton onClick={() => remove(index)}>
            <DeleteIcon htmlColor={grey[400]} />
          </IconButton>
          <IconButton onClick={() => setIsEditOpen(true)}>
            <EditIcon htmlColor={grey[400]} />
          </IconButton>
        </FlexBox>
      </FlexBox>
      {isEditOpen && (
        <FlexBox mt={2}>
          <AddOrEdit
            type="edit"
            isUnpublishTaken={
              isUnpublishTaken && field.type !== SurchargeType.unpublish
            }
            onSubmit={(stage) => update(index, stage)}
            onCancel={() => setIsEditOpen(false)}
            surcharge={field}
            currency={currency}
            takenDates={takenDatesWithoutEditable}
          />
        </FlexBox>
      )}
      <Divider />
    </FlexBox>
  );
}

const getChange = (field: IStage, currency: Currency) => {
  let change = "";
  if (field.type === SurchargeType.unpublish) {
    change = "(unpublish)";
  } else if (field.type === SurchargeType.fixedFee) {
    change = `(${getSign(field.amount || 0)} ${
      currencySymbols[currency]
    }${Math.abs(field.amount || 0)})`;
  } else {
    change = `(${getSign(field.amount || 0)} ${Math.abs(field.amount || 0)}%)`;
  }
  return change;
};

export default List;
